import React from "react";

type HorizontalValue = 'left' | 'center' | 'right';
type VerticalValue = 'top' | 'center' | 'bottom';
type AlignmentScope = 'desktop' | 'mobile';

interface SlideAlignmentSectionProps {
    horizontalAlignment: HorizontalValue;
    verticalAlignment: VerticalValue;
    useMobileAlignment: boolean;
    horizontalAlignmentMobile: HorizontalValue;
    verticalAlignmentMobile: VerticalValue;
    onFieldChange: (id: string, value: string | boolean) => void;
    onPreviewViewModeChange: (mode: AlignmentScope) => void;
}

const horizontalOptions: { label: string; value: HorizontalValue }[] = [
    { label: "Left", value: "left" },
    { label: "Center", value: "center" },
    { label: "Right", value: "right" },
];

const verticalOptions: { label: string; value: VerticalValue }[] = [
    { label: "Top", value: "top" },
    { label: "Center", value: "center" },
    { label: "Bottom", value: "bottom" },
];

export const CarouselSlideEditorAlignmentSection: React.FC<SlideAlignmentSectionProps> = ({
    horizontalAlignment,
    verticalAlignment,
    useMobileAlignment,
    horizontalAlignmentMobile,
    verticalAlignmentMobile,
    onFieldChange,
    onPreviewViewModeChange,
}) => {
    const renderAlignmentGrid = (
        scope: AlignmentScope,
        horizontalValue: HorizontalValue,
        verticalValue: VerticalValue,
        horizontalFieldId: string,
        verticalFieldId: string
    ) => (
        <div className="alignmentGrid">
            <div className="col">
                <div className="alignment-label">Horizontal Alignment</div>
                <p className="alignment-description">
                    Place the content group (title, description, logo and button) on the
                    left, center or right of the banner.
                </p>
                <div className="segmented-picker">
                    {horizontalOptions.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`picker-option ${horizontalValue === opt.value ? "active" : ""}`}
                            onClick={() => {
                                if (useMobileAlignment) onPreviewViewModeChange(scope);
                                onFieldChange(horizontalFieldId, opt.value);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="col">
                <div className="alignment-label">Vertical Alignment</div>
                <p className="alignment-description">
                    Place the content group at the top, middle or bottom of the banner.
                </p>
                <div className="segmented-picker">
                    {verticalOptions.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`picker-option ${verticalValue === opt.value ? "active" : ""}`}
                            onClick={() => {
                                if (useMobileAlignment) onPreviewViewModeChange(scope);
                                onFieldChange(verticalFieldId, opt.value);
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <section className="editSection editSection--alignment">
            <div className="editSection__head">
                <div className="title">Content Alignment</div>
            </div>

            {renderAlignmentGrid(
                "desktop",
                horizontalAlignment,
                verticalAlignment,
                "horizontalAlignment",
                "verticalAlignment"
            )}

            <label className="alignment-mobile-toggle">
                <input
                    type="checkbox"
                    checked={useMobileAlignment}
                    onChange={(e) => {
                        onFieldChange("useMobileAlignment", e.target.checked);
                        onPreviewViewModeChange(e.target.checked ? "mobile" : "desktop");
                    }}
                />
                <span>Use a different alignment on mobile</span>
            </label>

            {useMobileAlignment && (
                <div className="alignment-mobile-block">
                    <div className="alignment-subheading">Mobile</div>
                    {renderAlignmentGrid(
                        "mobile",
                        horizontalAlignmentMobile,
                        verticalAlignmentMobile,
                        "horizontalAlignmentMobile",
                        "verticalAlignmentMobile"
                    )}
                </div>
            )}
        </section>
    );
};
